import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ButtonNewRecord,
  FlexRow,
  TableDataState,
  TableEditButtons,
  toastWarning,
  usePaginatedQueryV8,
} from 'motion-components';
import { Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlCanAddIdoc, gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './filters';
import { DataGrid } from './grid';
import { handleExport } from './export-columns';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { observer } from 'mobx-react-lite';

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const IdocsGridPage = observer(
  ({
    collectionPointId,
    isGridInForm = false,
  }: {
    collectionPointId?: number;
    isGridInForm?: boolean;
  }) => {
    const { appStore, tableStore } = useRootStore();
    const { handleGraphqlError } = useHandleError('IdocsGridPage');

    const location = useLocation<any>();
    const history = useHistory();
    const [selected, setSelected] = useState<any>(null);
    const { t } = useTranslation();
    const [canAddIdoc] = useMutation(gqlCanAddIdoc);

    const canAddNew =
      appStore.isPlatformOwner || appStore.isCollector || appStore.isCollectorDriver;

    const handleAddNew = () => {};
    /* ------------------- Query ------------------------------- */
    const initOrderBy = 'id DESC';
    const query = usePaginatedQueryV8(
      gqlGetQuery,
      buildWhereFromFilterValues,
      location.state?.order ?? initOrderBy,
      appStore,
      true,
      { isActive: true, collectionPointId: collectionPointId },
      !isGridInForm, // History Replace
      !isGridInForm ? undefined : 'idocs_grid',
    );
    const {
      loading,
      data,
      dataCount,
      error,
      initPageIndex,
      filtersChanged,
      gridPageChanged,
      queryState,
      queryParams,
      setQueryParams,
      refetch,
    } = query;

    const client = useApolloClient();

    const canCreateNew = true;
    const handleCreateNew = useCallback(async () => {
      const historyPush = () =>
        history.push({
          pathname: generatePath(getRouteByName('Idoc'), { id: -1 }),
          state: {
            pathname: location.pathname,
            ...location.state,
          },
        });

      if (appStore.isCollector) {
        await canAddIdoc({ variables: { collectorId: appStore.associatedId } })
          .then((val) => {
            const canAdd = val.data.canAddIdoc;
            if (canAdd) historyPush();
            else
              toastWarning(
                'Η Άδεια Συλλογής Μεταφοράς σας έχει λήξει. Παρακαλούμε ανεβάστε το νέο έγγραφο άδειας και επικοινωνήστε με την Re-Battery για την απαραίτητη επικύρωση',
              );
          })

          .catch((error) => {
            handleGraphqlError('mutation-delete', error);
          });
      } else {
        historyPush();
      }
    }, [history, location.pathname, location.state]);

    const gridRef = useRef();

    if (error) {
      handleGraphqlError('query-get', error);
      return <h3 className="text-center">{error.message}</h3>;
    } else {
      return (
        <Card className="shadow">
          {!isGridInForm && (
            <Card.Header>
              <FlexRow>
                <Card.Title className="m-0">{t('Έντυπα Αναγνώρισης Αποβλήτων')}</Card.Title>
                {canAddNew && (
                  <ButtonNewRecord
                    onClick={handleCreateNew}
                    size="sm"
                    label={t('Νέο Έντυπο Αναγνώρισης')}
                  />
                )}
              </FlexRow>
            </Card.Header>
          )}

          <Card.Body className="nopadding">
            {/* Φίλτρα */}
            <Filters
              onChange={filtersChanged}
              initialValues={queryState?.filterValues ?? {}}
              disable={tableStore.state !== TableDataState.browse}
              isGridInForm={isGridInForm}
            />
            {/* Grid */}
            <DataGrid
              ref={gridRef}
              loading={loading}
              data={data}
              dataCount={dataCount}
              initPageIndex={initPageIndex}
              onPageChange={gridPageChanged}
              handleExport={() => {
                handleExport(client, queryState, t);
              }}
              initOrderBy={queryParams?.order ?? initOrderBy}
              onOrderChange={(orderBy) => {
                const newQueryParams = { ...queryParams, order: orderBy };
                setQueryParams(newQueryParams);
                history.replace({
                  pathname: location.pathname,
                  state: {
                    ...location.state,
                    order: orderBy,
                  },
                });
              }}
              selected={selected}
              setSelected={setSelected}
              refetch={refetch}
              isGridInForm={isGridInForm}
            />
          </Card.Body>
        </Card>
      );
    }
  },
);

